import { useMemo, useState } from "react";
import { Search, Loader2, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import SalviaLayout from "@/components/SalviaLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function I4hPlansAdmin() {
  usePageTitle("Planos I4H");
  const [searchTerm, setSearchTerm] = useState("");
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const utils = trpc.useUtils();

  const { data: planCatalog, isLoading } = trpc.lifeBase.listPlanCatalog.useQuery();
  const updateDescription = trpc.lifeBase.updatePlanDescription.useMutation({
    onSuccess: (plan) => {
      if (!plan) {
        toast.success("Descrição local atualizada.");
        void utils.lifeBase.listPlanCatalog.invalidate();
        return;
      }
      setDrafts((prev) => ({
        ...prev,
        [plan.id]: plan.localDescription || "",
      }));
      toast.success(`Descrição do plano ${plan.planName} atualizada.`);
      void utils.lifeBase.listPlanCatalog.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Não foi possível salvar a descrição local do plano.");
    },
  });

  const filteredPlans = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return planCatalog || [];
    }

    return (planCatalog || []).filter((plan) => {
      const haystack = [
        plan.planName,
        String(plan.i4hPlanId),
        plan.localDescription || "",
        plan.status,
      ].join(" ").toLowerCase();
      return haystack.includes(query);
    });
  }, [planCatalog, searchTerm]);

  const resolveDraft = (planId: number, currentValue: string | null | undefined) => {
    return drafts[planId] ?? currentValue ?? "";
  };

  const handleSave = async (planId: number, currentValue: string | null | undefined) => {
    const nextDescription = resolveDraft(planId, currentValue).trim();
    await updateDescription.mutateAsync({
      planCatalogId: planId,
      localDescription: nextDescription || null,
    });
  };

  return (
    <SalviaLayout title="Planos I4H">
      <div className="space-y-6">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
                <ShieldCheck className="h-3.5 w-3.5" />
                Administração da Plataforma
              </div>
              <h1 className="text-2xl font-semibold text-slate-900">Módulo de Planos I4H</h1>
              <p className="max-w-3xl text-sm leading-6 text-slate-600">
                Este módulo é consultivo e fica disponível somente para os papéis estruturais ADMIN. Aqui a plataforma mantém o catálogo persistido da I4H com uma descrição local orientativa para uso interno e para apoio ao wizard da Base de Pessoas.
              </p>
            </div>
            <div className="w-full max-w-md">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por nome do plano, ID I4H ou descrição"
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-2">
          {isLoading && (
            <div className="xl:col-span-2 rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
              Carregando catálogo de planos I4H...
            </div>
          )}

          {!isLoading && filteredPlans.length === 0 && (
            <div className="xl:col-span-2 rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500 shadow-sm">
              Nenhum plano corresponde aos filtros informados.
            </div>
          )}

          {filteredPlans.map((plan) => {
            const draft = resolveDraft(plan.id, plan.localDescription);
            const isDirty = draft !== (plan.localDescription || "");
            const isPending = updateDescription.isPending && updateDescription.variables?.planCatalogId === plan.id;

            return (
              <article key={plan.id} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold text-slate-900">{plan.planName}</h2>
                      <Badge variant="outline">I4H #{plan.i4hPlanId}</Badge>
                      <Badge variant={plan.status === "active" ? "default" : "secondary"}>{plan.status}</Badge>
                    </div>
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Fonte: {plan.source}</p>
                  </div>
                  <Button
                    type="button"
                    className="gap-2"
                    disabled={isPending || !isDirty}
                    onClick={() => handleSave(plan.id, plan.localDescription)}
                  >
                    {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Salvar descrição
                  </Button>
                </div>

                <div className="mt-5 space-y-2">
                  <label className="text-sm font-medium text-slate-900">Descrição local do plano</label>
                  <Textarea
                    value={draft}
                    onChange={(event) => setDrafts((prev) => ({ ...prev, [plan.id]: event.target.value }))}
                    placeholder="Descreva de forma simples quando este plano deve ser escolhido, sem alterar o nome oficial vindo da I4H."
                    rows={5}
                    disabled={isPending}
                  />
                  <p className="text-xs leading-5 text-slate-500">
                    O nome oficial do plano continua sincronizado com a origem I4H. Neste módulo, a edição é limitada apenas à descrição local utilizada para orientar times administrativos e o wizard da Base de Pessoas.
                  </p>
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </SalviaLayout>
  );
}
